import * as XLSX from 'xlsx';
import { HistoricDriverEvent } from 'src/app/models/historic-service/historic-service.model';

/**
 * Horas "HH:mm" de los cuatro hitos del conductor, en el orden de las columnas del export:
 * inicio de recogida, fin de recogida, inicio de devolución y fin de devolución.
 * Cadena vacía cuando el hito no existe.
 */
export type DriverStageTimes = [
  pickupStart: string,
  pickupEnd: string,
  returnStart: string,
  returnEnd: string
];

/** Número de columnas de hora del conductor que añade el export. */
export const DRIVER_STAGE_COLUMNS = 4;

// El back guarda el texto del evento ya redactado ("{conductor} ha iniciado la recogida"),
// y es el único dato que distingue recogida de devolución; se compara sin acentos.
const STAGE_MARKERS: readonly string[] = [
  'ha iniciado la recogida',
  'ha finalizado la recogida',
  'ha iniciado la devolucion',
  'ha finalizado la devolucion'
];

function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/**
 * Hora local de cada hito del conductor. Se queda con la primera ocurrencia de cada uno
 * (el back manda los eventos en orden cronológico); los eventos del proveedor, los fallidos
 * y los comentarios no tienen columna y se ignoran.
 */
export function getDriverStageTimes(
  events?: HistoricDriverEvent[] | null
): DriverStageTimes {
  const times: DriverStageTimes = ['', '', '', ''];
  for (const event of events ?? []) {
    // Al final del texto, para no confundir un comentario o un motivo libre que mencione el hito.
    const stage = STAGE_MARKERS.findIndex((marker) =>
      normalize(event.reason ?? '').trim().endsWith(marker)
    );
    const time = (event.date?.time ?? '').slice(0, 5);
    if (stage >= 0 && /^\d{2}:\d{2}$/.test(time) && !times[stage]) {
      times[stage] = time;
    }
  }
  return times;
}

/**
 * "HH:mm" como fracción de día, que es como Excel guarda una hora: así se pueden
 * restar dos celdas para sacar la duración. Null si el texto no es una hora.
 */
export function timeToExcelDay(time: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  return match ? (Number(match[1]) * 60 + Number(match[2])) / 1440 : null;
}

/** Aplica el formato hh:mm a las celdas numéricas de las columnas indicadas (sin la cabecera). */
export function applyTimeFormat(
  sheet: XLSX.WorkSheet,
  firstColumn: number,
  columnCount: number
): void {
  const range = XLSX.utils.decode_range(sheet['!ref'] ?? 'A1');
  for (let row = range.s.r + 1; row <= range.e.r; row++) {
    for (let col = firstColumn; col < firstColumn + columnCount; col++) {
      const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })];
      if (cell && cell.t === 'n') {
        cell.z = 'hh:mm';
      }
    }
  }
}
